import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import Tree from '../../../lib/tree';
import TreeNode from './TreeNode';
import { DISABLED_FIELD, MORE_KEY, Select } from '../select/Select';
import normalizeTreeNodes from './normalizeTreeNodes';
import autobind from '../_util/autobind';
import isIE from '../_util/isIE';
import { defaultRenderer } from '../tree';
import { getTreeNodes } from '../tree/util';
import Icon from '../icon';
let TreeSelect = class TreeSelect extends Select {
    constructor() {
        super(...arguments);
        this.stateForceRenderKeys = [];
    }
    get forceRenderKeys() {
        if (this.expandedKeys) {
            this.stateForceRenderKeys = [
                ...new Set([...this.stateForceRenderKeys, ...this.expandedKeys]),
            ];
        }
        else {
            this.stateForceRenderKeys = this.props.treeDefaultExpandedKeys || [];
        }
        return this.stateForceRenderKeys;
    }
    get parentField() {
        return this.getProp('parentField') || 'parentValue';
    }
    get idField() {
        return this.getProp('idField') || this.valueField;
    }
    get options() {
        const { field, textField, valueField, idField, parentField, multiple, observableProps: { children, options }, } = this;
        return (options ||
            (field && field.options) ||
            normalizeTreeNodes({ textField, valueField, disabledField: DISABLED_FIELD, parentField, idField, multiple, children }));
    }
    get treeData() {
        return this.optionsWithCombo.filter(r => !r.parent);
    }
    get selectedKeys() {
        const { idField } = this;
        return this.options.reduce((array, r) => this.isSelected(r) ? array.concat(String(r.get(idField))) : array, []);
    }
    handleSearch(text) {
        if (text) {
            const { options } = this;
            const { idField } = options.props;
            this.expandedKeys = options.map((r) => String(r.get(idField)));
        }
    }
    handleExpand(keys) {
        this.expandedKeys = keys;
    }
    handleTreeSelect(_e, { node }) {
        const { record, disabled, key } = node;
        if (key === MORE_KEY) {
            const { options } = this;
            options.queryMore(options.currentPage + 1);
        }
        else if (!disabled) {
            const { multiple } = this;
            if (multiple) {
                if (this.isSelected(record)) {
                    const records = record.treeReduce((array, r) => this.isSelected(r) ? array.concat(r) : array, []);
                    const parents = record.parents.filter(parent => this.isSelected(parent));
                    this.unChoose(records.concat(parents));
                }
                else {
                    const records = record.treeReduce((array, r) => this.isSelected(r) ? array : array.concat(r), []);
                    const parents = [];
                    record.parents.every((parent, index) => {
                        const { children } = parent;
                        if (children && children.every(child => {
                            return (index === 0 ? records.includes(child) : parents.includes(child)) || this.isSelected(child);
                        })) {
                            parents.push(parent);
                            return true;
                        }
                        return false;
                    });
                    this.choose(records.concat(parents));
                }
            }
            else {
                this.choose(record);
            }
        }
    }
    handleTreeNode(e) {
        const { onOption } = this.props;
        const props = onOption(e) || {};
        props.className = classNames(props.className, `${this.getMenuPrefixCls()}-item`);
        return props;
    }
    handleLoadData(event) {
        const { loadData } = this.props;
        const dataSet = this.options;
        const promises = [];
        if (dataSet) {
            const { idField, parentField } = dataSet.props;
            const { record } = event.props;
            if (idField && parentField && record && !record.children) {
                const id = record.get(idField);
                promises.push(dataSet.queryMore(-1, { [parentField]: id }));
            }
        }
        if (loadData) {
            promises.push(loadData(event));
        }
        return Promise.all(promises);
    }
    getMenu(menuProps = {}) {
        const { options, } = this;
        const menuPrefixCls = this.getMenuPrefixCls();
        const { textField, selectedKeys, expandedKeys, multiple, forceRenderKeys, text, props: { dropdownMenuStyle, optionRenderer = defaultRenderer, optionsFilter, treeDefaultExpandAll, treeDefaultExpandedKeys, treeCheckable, async, loadData, }, } = this;
        const menuDisabled = this.isDisabled();
        /**
         * fixed when ie the scroll width would cover the item width
         */
        const IeMenuStyle = !this.dropdownMatchSelectWidth && isIE() ? { padding: '.08rem' } : {};
        const treeData = getTreeNodes(options, this.treeData, [...forceRenderKeys.values()], optionRenderer, 
        // @ts-ignore
        this.handleTreeNode, async || !!loadData, textField, treeDefaultExpandAll, optionsFilter, this.matchRecordBySearch, text);
        if (!treeData || !treeData.length) {
            return (React.createElement("div", { className: menuPrefixCls },
                React.createElement("div", { className: `${menuPrefixCls}-item ${menuPrefixCls}-item-disabled` }, this.loading ? ' ' : this.getNotFoundContent())));
        }
        if (options.paging && options.currentPage < options.totalPage) {
            treeData.push({
                key: MORE_KEY,
                eventKey: MORE_KEY,
                title: React.createElement(Icon, { type: "more_horiz" }),
                className: `${this.getMenuPrefixCls()}-item ${menuPrefixCls}-item-more`,
                isLeaf: true,
            });
        }
        const props = {};
        if (expandedKeys) {
            props.expandedKeys = expandedKeys.slice();
        }
        return (React.createElement(Tree, Object.assign({ ref: this.saveMenu, ripple: true, disabled: menuDisabled, onSelect: this.handleTreeSelect, onCheck: this.handleTreeSelect, onExpand: this.handleExpand, style: { ...IeMenuStyle, ...dropdownMenuStyle }, selectable: true, focusable: false, treeData: treeData, defaultExpandAll: treeDefaultExpandAll, defaultExpandedKeys: treeDefaultExpandedKeys, selectedKeys: selectedKeys, checkedKeys: selectedKeys, checkable: 'treeCheckable' in this.props ? treeCheckable : multiple, className: menuPrefixCls, multiple: multiple, loadData: async ? this.handleLoadData : loadData }, props, menuProps)));
    }
};
TreeSelect.displayName = 'TreeSelect';
TreeSelect.propTypes = {
    treeCheckable: PropTypes.bool,
    treeDefaultExpandAll: PropTypes.bool,
    treeDefaultExpandedKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    ...Select.propTypes,
};
TreeSelect.defaultProps = {
    ...Select.defaultProps,
    suffixCls: 'tree-select',
    dropdownMatchSelectWidth: false,
    reverse: false,
};
TreeSelect.TreeNode = TreeNode;
__decorate([
    observable
], TreeSelect.prototype, "expandedKeys", void 0);
__decorate([
    computed
], TreeSelect.prototype, "forceRenderKeys", null);
__decorate([
    computed
], TreeSelect.prototype, "parentField", null);
__decorate([
    computed
], TreeSelect.prototype, "idField", null);
__decorate([
    computed
], TreeSelect.prototype, "options", null);
__decorate([
    computed
], TreeSelect.prototype, "treeData", null);
__decorate([
    computed
], TreeSelect.prototype, "selectedKeys", null);
__decorate([
    action
], TreeSelect.prototype, "handleSearch", null);
__decorate([
    autobind,
    action
], TreeSelect.prototype, "handleExpand", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleTreeSelect", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleTreeNode", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleLoadData", null);
__decorate([
    autobind
], TreeSelect.prototype, "getMenu", null);
TreeSelect = __decorate([
    observer
], TreeSelect);
export default TreeSelect;
